import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Phone, MessageSquare, Mail, Download, FileText, Check } from "lucide-react";
import { WHOLESALE_MIN, WHOLESALE_PHONE, WHOLESALE_PHONE_RAW, WHOLESALE_EMAIL } from "../data/wholesale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = "/Balance_Beauty_Wholesale.pdf";
const money = (n) => `$${Number(n).toFixed(0)}`;

function triggerDownload() {
  const a = document.createElement("a");
  a.href = PDF_URL;
  a.download = "";
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function DownloadCapture({ dark, submitTestId }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const valid = /\S+@\S+\.\S+/.test(email);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/wholesale/download`, { email, company: company || null });
    } catch (_) {
      /* still let them download */
    } finally {
      setBusy(false);
      setDone(true);
      triggerDownload();
    }
  };

  const inputCls = dark
    ? "w-full bg-white/[0.08] border border-white/20 focus:border-white/60 text-white placeholder:text-white/40 outline-none rounded-full px-5 py-3.5 font-body text-sm transition-colors"
    : "w-full bg-transparent border border-[var(--line)] focus:border-[var(--ink)] outline-none rounded-full px-5 py-3.5 font-body text-sm transition-colors";

  if (done) {
    return (
      <div className={dark ? "text-white" : "text-[var(--ink)]"} data-testid="wholesale-download-done">
        <div className="flex items-center gap-2 font-display text-xl">
          <Check size={20} className="text-[var(--terra-soft)]" /> Your price sheet is downloading.
        </div>
        <a
          href={PDF_URL}
          download
          target="_blank"
          rel="noopener noreferrer"
          onClick={triggerDownload}
          className={`overline underline mt-2 inline-block ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}
          data-testid="wholesale-download-again"
        >
          Didn't start? Download again
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-md" data-testid="wholesale-download-form">
      <div className="grid gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className={inputCls}
          data-testid="wholesale-download-email"
        />
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company (optional)"
          className={inputCls}
          data-testid="wholesale-download-company"
        />
        <button
          type="submit"
          disabled={!valid || busy}
          className="inline-flex items-center justify-center gap-2 bg-[var(--terra)] text-white px-8 py-4 rounded-full hover:scale-[1.02] transition-transform text-base disabled:opacity-40 disabled:hover:scale-100"
          data-testid={submitTestId}
        >
          <Download size={18} /> {busy ? "Preparing…" : "Download price sheet (PDF)"}
        </button>
      </div>
      <p className={`font-body text-xs mt-2 ${dark ? "text-white/40" : "text-[var(--ink-soft)]"}`}>
        Enter your email to get the sheet — we'll follow up with your wholesale pricing.
      </p>
    </form>
  );
}

export default function WholesaleOrder() {
  return (
    <div className="min-h-screen bg-[var(--bone)] text-[var(--ink)]" data-testid="wholesale-order-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[var(--bone)]/90 backdrop-blur-md border-b border-[var(--line)]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 overline" data-testid="wholesale-back-home">
            <ArrowLeft size={16} /> Back to store
          </Link>
          <span className="font-display text-xl">Balance <span className="text-[var(--terra)]">+ Beauty</span></span>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-[var(--ink)] text-[var(--bone)]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <p className="overline text-[var(--terra-soft)] mb-5">Verified partners · Minimum order {money(WHOLESALE_MIN)}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
            Wholesale<br />price sheet.
          </h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-8 max-w-2xl leading-relaxed">
            Download our full wholesale price sheet below. Wholesale is available only on orders over{" "}
            <b className="text-white">{money(WHOLESALE_MIN)}</b>, and orders above that threshold are deeply
            discounted (the deeper pricing applies to the amount over {money(WHOLESALE_MIN)}).
          </p>

          <div className="mt-9">
            <DownloadCapture dark submitTestId="wholesale-download-pdf" />
          </div>
        </div>
      </div>

      {/* Download card + contact */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="border border-[var(--line)] bg-[var(--bone-2)] rounded-2xl p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8 justify-between">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[var(--ink)] text-[var(--bone)] flex items-center justify-center shrink-0">
              <FileText size={28} />
            </div>
            <div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Balance + Beauty — Wholesale Price Sheet</h2>
              <p className="font-body text-sm text-[var(--ink-soft)] mt-1">The complete, up-to-date catalog and pricing — PDF.</p>
            </div>
          </div>
          <div className="w-full lg:w-auto lg:min-w-[320px]">
            <DownloadCapture submitTestId="wholesale-download-pdf-card" />
          </div>
        </div>

        {/* Contact */}
        <div className="mt-14 text-center">
          <h3 className="font-display text-3xl sm:text-4xl tracking-tight">Ready to place a wholesale order?</h3>
          <p className="font-body text-[var(--ink-soft)] mt-3 max-w-lg mx-auto">
            Orders over {money(WHOLESALE_MIN)} only. Reach out with your item codes and quantities — we'll confirm your deeply discounted total.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-7" data-testid="wholesale-contact">
            <a href={`tel:${WHOLESALE_PHONE_RAW}`} className="btn-ink px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="wholesale-call"><Phone size={16} /> Call {WHOLESALE_PHONE}</a>
            <a href={`sms:${WHOLESALE_PHONE_RAW}`} className="btn-outline px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="wholesale-text"><MessageSquare size={16} /> Text {WHOLESALE_PHONE}</a>
            <a href={`mailto:${WHOLESALE_EMAIL}?subject=Wholesale%20order%20(over%20%24500)`} className="btn-outline px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="wholesale-email"><Mail size={16} /> {WHOLESALE_EMAIL}</a>
          </div>
          <p className="font-mono text-xs text-[var(--ink-soft)] mt-10 max-w-xl mx-auto">
            Research compounds are sold strictly for laboratory research purposes and are not for human consumption.
            Prices subject to change and availability.
          </p>
        </div>
      </div>
    </div>
  );
}
